import React, { useState } from "react";
import { useRouter } from "next/router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#next/components/ui/card";
import { Button } from "#next/components/ui/button";
import { useSubscription } from "#next/hooks/use-subscription";
import { useSubscriptionManagement } from "#next/hooks/use-stripe-checkout";
import { toast } from "#next/lib/toast";
import { usePricingTiers } from "#next/hooks/use-pricing-tiers";
import { CheckoutButton } from "#next/components/stripe/checkout-button";
import { UTCDate } from "@date-fns/utc";
import { addMonths } from "date-fns/addMonths";

export function SubscriptionManagement() {
  const router = useRouter();
  const { data: subscription, isLoading: isSubscriptionLoading } = useSubscription();
  const { pricingTiers, currentTierProductId, isLoading: isProductsLoading } = usePricingTiers();
  const [ showConfirmCancel, setShowConfirmCancel ] = useState(false);

  const { goToCustomerPortal, cancelSubscription, isLoading: isActionLoading, reactivateSubscription } = useSubscriptionManagement({
    onSuccess: (message) => {
      toast.success(message);
      setShowConfirmCancel(false);
    },
    onError: (error) => {
      toast.error(error);
    }
  });

  // const { handleCheckout, isLoading: isCheckoutLoading } = useStripeCheckout();

  // Check URL params for checkout direct link
  React.useEffect(() => {
    const checkoutParam = router.query.checkout;

    if (checkoutParam === process.env.NEXT_PUBLIC_STRIPE_PRO_ID && !isSubscriptionLoading && subscription?.tier !== "pro") {
      // handleCheckout();
      // Remove the parameter after processing
      router.replace("/", undefined, { shallow: true });
    }
  }, [ router.query, subscription, isSubscriptionLoading ]);

  if (isSubscriptionLoading || isProductsLoading) {
    return (
      <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50 shadow-lg overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
          <CardTitle className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 3a6 6 0 0 0-6 6c0 2.17.974 4.103 2.5 5.444C13.42 16.295 15 18.044 15 21h0a6 6 0 0 0 6-6c0-2.17-.974-4.103-2.5-5.444C16.58 7.705 15 5.956 15 3h0z"/>
              <path d="M9 3a6 6 0 0 1 6 6c0 2.17-.974 4.103-2.5 5.444C10.58 16.295 9 18.044 9 21h0a6 6 0 0 1-6-6c0-2.17.974-4.103 2.5-5.444C7.42 7.705 9 5.956 9 3h0z"/>
            </svg>
            Subscription Management
          </CardTitle>
          <CardDescription className="text-purple-100">
            Loading your subscription information...
          </CardDescription>
        </CardHeader>
        <CardContent className="p-8 flex justify-center">
          <div className="flex justify-center items-center h-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Helper function to determine if a tier is available for the user to switch to
  const isTierAvailable = (tier: string): boolean => {
    return tier !== subscription?.tier;
  };

  return (
    <Card data-section="pricing-plans" className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50 shadow-lg overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <CardTitle className="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 3a6 6 0 0 0-6 6c0 2.17.974 4.103 2.5 5.444C13.42 16.295 15 18.044 15 21h0a6 6 0 0 0 6-6c0-2.17-.974-4.103-2.5-5.444C16.58 7.705 15 5.956 15 3h0z"/>
            <path d="M9 3a6 6 0 0 1 6 6c0 2.17-.974 4.103-2.5 5.444C10.58 16.295 9 18.044 9 21h0a6 6 0 0 1-6-6c0-2.17.974-4.103 2.5-5.444C7.42 7.705 9 5.956 9 3h0z"/>
          </svg>
          Subscription Management
        </CardTitle>
        <CardDescription className="text-purple-100">
          Manage your subscription and billing
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <div className="mb-6 bg-gradient-to-r from-purple-100/80 to-indigo-100/80 p-4 rounded-lg border border-purple-200">
          <div className="flex items-start gap-3">
            <div className="mt-1 text-purple-600 bg-purple-200 rounded-full p-1.5 flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4"/>
                <path d="M12 8h.01"/>
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-purple-800">Current Plan: {subscription?.tier === "pro" ? "Pro" : subscription?.tier === "enterprise" ? "Enterprise" : "Free"}</h4>
              <p className="text-purple-700 text-sm mt-1">
                {subscription?.tier !== "free"
                  ? subscription?.willCancelOn
                    ? `Your subscription will cancel on ${new UTCDate(subscription.willCancelOn).toLocaleDateString()}.`
                    : `Your subscription renews on ${addMonths(new UTCDate(subscription?.createdAt!), 1).toLocaleDateString()}.`
                  : "You're currently on the Free plan. Upgrade for additional features."}
              </p>
            </div>
          </div>
        </div>
        {subscription?.tier !== "free" && (
          <div className="mb-6 space-y-4">
            {showConfirmCancel ? (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                <h3 className="text-red-700 font-bold mb-2">Are you sure you want to cancel?</h3>
                <p className="text-red-600 mb-4">
                  If you cancel, your subscription will be immediately downgraded to the free tier.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="destructive"
                    onClick={() => cancelSubscription()}
                    disabled={isActionLoading}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {isActionLoading ? "Processing..." : "Confirm Cancellation"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowConfirmCancel(false)}
                    disabled={isActionLoading}
                  >
                    Keep Subscription
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => goToCustomerPortal()}
                  disabled={isActionLoading}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {isActionLoading ? "Processing..." : "Manage Billing"}
                </Button>
                {subscription?.willCancelOn ? (
                  <Button
                    onClick={() => reactivateSubscription()}
                    disabled={isActionLoading}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {isActionLoading ? "Processing..." : "Reactivate Plan"}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setShowConfirmCancel(true)}
                    disabled={isActionLoading}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    {isActionLoading ? "Processing..." : "Cancel Subscription"}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          {pricingTiers.map((tier) => (
            <div
              key={tier.name}
              className={`bg-white rounded-lg overflow-hidden shadow-md border-2 flex flex-col h-full ${
                tier.id === currentTierProductId
                  ? "border-violet-400"
                  : "border-gray-200"
              }`}
            >
              <div
                className={`p-4 bg-gradient-to-r ${tier.color} text-white`}>
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-lg">{tier.name}</h3>
                  {tier.id === currentTierProductId && (
                    <div className="top-0 right-0 -mt-6 -mr-6 bg-gradient-to-r from-green-400 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                      Current Plan
                    </div>
                  )}
                </div>
                <div className="mt-2 text-2xl font-bold">
                  {tier.monthlyPrice}{tier.monthlyPrice !== "$0" && <span className="text-sm font-normal">/month</span>}
                </div>
              </div>
              <div className="p-4 flex-grow">
                <div className="space-y-2">
                  {tier.usageDetails?.map((detail, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <div
                        className={`flex-shrink-0 mt-1 p-0.5 rounded-full ${
                          tier.tier === "pro" ? "text-violet-600" : tier.tier === "enterprise" ? "text-emerald-600" : "text-blue-600"
                        }`}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6 9 17l-5-5"/>
                        </svg>
                      </div>
                      <span
                        className={`text-sm ${
                          tier.tier === "pro" ? "text-violet-800" : tier.tier === "enterprise" ? "text-emerald-800" : "text-blue-800"
                        }`}>
                        {detail}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-4 mt-auto">
                {tier.id === currentTierProductId ? (
                  <>
                    {subscription?.willCancelOn && (
                      <Button
                        className={`w-full py-6 text-base font-semibold bg-gradient-to-r ${tier.color} hover:opacity-80 hover:scale-105 text-white shadow-lg hover:shadow-xl transition-all border`}
                        onClick={() => reactivateSubscription()}
                        disabled={isActionLoading}
                      >
                        {isActionLoading ? "Processing..." : "Reactivate Plan"}
                      </Button>
                    )}
                  </>
                ) : isTierAvailable(tier.tier) && (
                  tier.tier === "free" ? (
                    <Button
                      className={`w-full py-6 text-base font-semibold bg-gradient-to-r ${tier.color} hover:opacity-80 hover:scale-105 text-white shadow-lg hover:shadow-xl transition-all border`}
                      onClick={() => {
                        setShowConfirmCancel(true);
                        // Implement free tier logic - could be a call to an API to cancel subscription
                        // alert("This will immediately downgrade your subscription to the free tier.");
                      }}
                    >
                      {tier.cta}
                    </Button>
                  ) : (
                    <CheckoutButton
                      priceIds={tier.priceIds || []}
                      className={`w-full py-6 text-base font-semibold bg-gradient-to-r ${tier.color} hover:opacity-80 hover:scale-105 text-white shadow-lg hover:shadow-xl transition-all border`}
                    >
                      {tier.cta}
                    </CheckoutButton>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}